class PrimerryReviews extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.tabs = Array.from(this.querySelectorAll('[data-review-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-review-panel]'));
    this.swipers = new Map();
    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleBlockSelect = this.handleBlockSelect.bind(this);
    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);
    this.addEventListener('shopify:block:select', this.handleBlockSelect);
    this.initSwipers();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
    this.removeEventListener('shopify:block:select', this.handleBlockSelect);
    this.swipers.forEach((swiper) => swiper.destroy(true, true));
    this.swipers.clear();
  }

  async initSwipers() {
    try {
      const SwiperClass = await this.waitForSwiper();
      if (!this.isConnected) return;
      this.querySelectorAll('[data-review-swiper]').forEach((element) => {
        if (this.swipers.has(element)) return;
        // The homepage review rail, [data-review-swiper] inside .primerry-reviews__swiper:
        // no cloned slides and no wrap-around, per Kanak 16 Sep 2026. addLoopSlides padded
        // the rail to 24 cards so it never reached an end; the cards now stop at the last one.
        const swiper = new SwiperClass(element, {
          slidesPerView: 'auto',
          centeredSlides: false,
          loop: false,
          initialSlide: Number(element.dataset.initialSlide) || 0,
          spaceBetween: 8,
          speed: 650,
          grabCursor: true,
          simulateTouch: true,
          allowTouchMove: true,
          threshold: 4,
          touchRatio: 1,
          resistance: true,
          resistanceRatio: 0.72,
          longSwipesRatio: 0.15,
          longSwipesMs: 250,
          watchOverflow: true,
          freeMode: {
            enabled: true,
            momentum: true,
            momentumBounce: false,
            momentumRatio: 0.82,
            momentumVelocityRatio: 0.8,
            minimumVelocity: 0.02,
          },
          mousewheel: {
            enabled: true,
            forceToAxis: true,
            releaseOnEdges: true,
            sensitivity: 0.65,
            thresholdDelta: 2,
          },
          breakpoints: {
            750: { spaceBetween: 12 },
          },
          observer: true,
          observeParents: true,
          resizeObserver: true,
        });
        this.swipers.set(element, swiper);
      });
    } catch (error) {
      this.dataset.swiperFallback = 'true';
    }
  }

  addLoopSlides(element) {
    const wrapper = element.querySelector('.swiper-wrapper');
    const originals = Array.from(wrapper?.children || []).filter((slide) => !slide.dataset.reviewClone);
    if (!wrapper || originals.length < 2) return;

    const minimumSlideCount = 24;
    let cloneIndex = 0;

    while (wrapper.children.length < minimumSlideCount) {
      const clone = originals[cloneIndex % originals.length].cloneNode(true);
      clone.dataset.reviewClone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-review-block');
      clone.removeAttribute('data-review-index');
      clone.removeAttribute('data-shopify-editor-block');
      wrapper.appendChild(clone);
      cloneIndex += 1;
    }
  }

  waitForSwiper() {
    if (window.Swiper) return Promise.resolve(window.Swiper);
    return new Promise((resolve, reject) => {
      const startedAt = performance.now();
      const check = () => {
        if (window.Swiper) {
          resolve(window.Swiper);
          return;
        }
        if (performance.now() - startedAt >= 10000) {
          reject(new Error('Swiper failed to load.'));
          return;
        }
        requestAnimationFrame(check);
      };
      check();
    });
  }

  handleClick(event) {
    const tab = event.target.closest('[data-review-tab]');
    if (!tab || !this.contains(tab)) return;
    this.activateTab(tab.dataset.reviewTab, true);
  }

  handleKeydown(event) {
    const current = event.target.closest('[data-review-tab]');
    if (!current || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    let index = this.tabs.indexOf(current);
    if (event.key === 'Home') index = 0;
    if (event.key === 'End') index = this.tabs.length - 1;
    if (event.key === 'ArrowRight') index = (index + 1) % this.tabs.length;
    if (event.key === 'ArrowLeft') index = (index - 1 + this.tabs.length) % this.tabs.length;
    const next = this.tabs[index];
    this.activateTab(next.dataset.reviewTab, true);
    next.focus({ preventScroll: true });
  }

  activateTab(handle, scrollTab) {
    this.tabs.forEach((tab) => {
      const active = tab.dataset.reviewTab === handle;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
      if (active && scrollTab) tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    });

    this.panels.forEach((panel) => {
      const active = panel.dataset.reviewPanel === handle;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
      if (!active) return;
      requestAnimationFrame(() => {
        const element = panel.querySelector('[data-review-swiper]');
        const swiper = this.swipers.get(element);
        if (!swiper) return;
        swiper.update();
        swiper.slideTo(Number(element.dataset.initialSlide) || 0, 0);
      });
    });
  }

  handleBlockSelect(event) {
    const block = this.querySelector(`[data-review-block="${CSS.escape(event.detail.blockId)}"]`);
    if (!block) return;
    const panel = block.closest('[data-review-panel]');
    this.activateTab(panel.dataset.reviewPanel, true);
    requestAnimationFrame(() => {
      const element = panel.querySelector('[data-review-swiper]');
      this.swipers.get(element)?.slideTo(Number(block.dataset.reviewIndex) || 0, 350);
    });
  }
}

if (!customElements.get('primerry-reviews')) {
  customElements.define('primerry-reviews', PrimerryReviews);
}
